import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { PriorityLevel,
  WellnessWorkOrder,
  PriorityLevelGenSvc,
  WorkWorkOrder,
  QuoteWorkOrder,
  Employee,
  EquipmentType,
  ZipCode,
} from '../../services_autogenerated/generated_services';
import { WorkOrderFilterPipe } from 'src/app/pipes/work-order-filter.pipe';
import { FilteredWellnessWorkOrder } from 'src/app/models/filteredWellnessWorkOrder';
import { FilteredWorkWorkOrder } from 'src/app/models/filteredWorkWorkOrder';
import { FilteredQuoteWorkOrder } from 'src/app/models/filteredQuoteWorkOrder';
import { CalculatedPriorityType } from 'src/app/models/calculatedPriorityType';
import { DragAndDropService } from 'src/app/services/drag-and-drop.service';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-work-order-filter-bar',
    templateUrl: './work-order-filter-bar.component.html',
    styleUrls: ['./work-order-filter-bar.component.css'],
    standalone: false
})
export class WorkOrderFilterBarComponent implements OnInit, OnChanges {
  @Input() isDayView: boolean;
  @Input() typeOfWorkOrders: ('quote' | 'work' | 'wellness') = null;
  @Input() allWorkOrders: (WellnessWorkOrder | WorkWorkOrder | QuoteWorkOrder)[] = [];
  @Input() rms: Employee[];
  @Input() equipmentTypes: string[];
  @Input() useSearchDelay = true;
  @Input() isWellnessRenewal: boolean = false;
  @Input() zipCodes: ZipCode[];
  @Input() hideRMAndZip: boolean = false;
  @Input() showRefresh: boolean = false;

  searchInput = new Subject<string>();

  priorityLevels: PriorityLevel[];
  searchTerm = '';

  filteredWorkOrders: (FilteredWellnessWorkOrder | FilteredWorkWorkOrder | FilteredQuoteWorkOrder)[];

  shouldFilterByDueDate = false;
  dueDateFilterFromDate: Date;
  dueDateFilterToDate: Date;

  shouldFilterByGeoRegion = false;
  geoRegionSearchRadius: number;
  geoRegionWorkOrderNumber: string;
  geoRegionWorkOrderFindError: string;
  geoRegionWorkOrderAddressError: string;

  shouldFilterByPriority = false;
  selectedPriorities: PriorityLevel[] = [];

  shouldFilterByRM = false;
  selectedRMs: Employee[] = [];
  
  shouldFilterByGDD = false;
  gddStart: number;
  gddEnd: number;

  shouldFilterByEquipment = false;
  selectedEquipment: string;

  shouldFilterByAutoRenewalSelected = false;

  shouldFilterByNeedsRenewed = false;

  shouldFilterByQuoteDeclined = false; // for wellness renewal page only

  shouldFilterByCreatedDate = false;
  createdDateStart: Date;
  createdDateEnd: Date;

  shouldFilterByCompletedDate = false;
  completedDateStart: Date;
  completedDateEnd: Date;

  shouldFilterByZip = false;
  selectedZipCodes: ZipCode[] = [];

  @Output() OnWoFilteringFinished = new EventEmitter<(FilteredWellnessWorkOrder | FilteredWorkWorkOrder | FilteredQuoteWorkOrder)[]>();
  @Output() forceRefresh = new EventEmitter();

  constructor(
    private priorityService: PriorityLevelGenSvc,
    private dragAndDropService: DragAndDropService,
    private workOrderFilter: WorkOrderFilterPipe,
  ) {
   }

  ngOnInit() {
    this.setUpSearchDelay();
    this.priorityService.getAll().subscribe(p => {

      const keys = Object.keys(CalculatedPriorityType);
      const values = keys.map(el => Object(CalculatedPriorityType)[el]);

      const calculatedPriorityTypes = values.map(x => new PriorityLevel({
        id: -1,
        active: true,
        status: x.toString(),
      })).filter(x => x.status !== 'Error');
      this.priorityLevels = calculatedPriorityTypes.concat(p);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.allWorkOrders && this.allWorkOrders) {
      this.filterWorkOrders();
    }
  }

  setUpSearchDelay() {
    if (this.useSearchDelay) {
      this.searchInput.pipe(
        debounceTime(600),
        distinctUntilChanged())
        .subscribe(value => {
          this.filterWorkOrders();
        });
    } else {
      this.searchInput.pipe(
        distinctUntilChanged())
        .subscribe(value => {
          this.filterWorkOrders();
        });
    }
  }

  filterWorkOrders(forceRefresh: boolean = false): void {
    if (this.allWorkOrders) {
      let temp;
      const isWellnessOnly = this.typeOfWorkOrders === 'wellness';
      const isTreeWorkOnly = this.typeOfWorkOrders === 'work';
      const isQuote = this.typeOfWorkOrders === 'quote';

      if (isWellnessOnly) {
        if (this.searchTerm) {
          temp = this.workOrderFilter.transform(this.allWorkOrders as WellnessWorkOrder[], this.searchTerm) as FilteredWellnessWorkOrder[];
        } else {
          temp = this.allWorkOrders as FilteredWellnessWorkOrder[];
        }
      } else if (isTreeWorkOnly) {
        if (this.searchTerm) {
          temp = this.workOrderFilter.transformWork(this.allWorkOrders as WorkWorkOrder[], this.searchTerm) as FilteredWorkWorkOrder[];
        } else {
          temp = this.allWorkOrders as FilteredWorkWorkOrder[];
        }
      } else if (isQuote) {
        if (this.searchTerm) {
          temp = this.workOrderFilter.transformQuote(this.allWorkOrders as QuoteWorkOrder[], this.searchTerm) as FilteredQuoteWorkOrder[];
        } else {
          temp = this.allWorkOrders as FilteredQuoteWorkOrder[];
        }
      }

      if (this.shouldFilterByGDD && isWellnessOnly) {
        temp = this.workOrderFilter.filterByGDD(temp, this.gddStart, this.gddEnd);
      }
      if (this.shouldFilterByDueDate && moment(this.dueDateFilterFromDate).isSameOrBefore(moment(this.dueDateFilterToDate))) {
        temp = this.workOrderFilter.filterByDueDate(temp, this.dueDateFilterFromDate, this.dueDateFilterToDate);
      }
      if (this.shouldFilterByCreatedDate && moment(this.createdDateStart).isSameOrBefore(moment(this.createdDateEnd))) {
        temp = this.workOrderFilter.filterByCreatedDate(temp, this.createdDateStart, this.createdDateEnd);
      }
      if (this.shouldFilterByGeoRegion) {
        let searchWorkOrder = this.allWorkOrders.find(x =>
          this.geoRegionWorkOrderNumber ? x.quoteNumber.toLowerCase().includes(this.geoRegionWorkOrderNumber.toLowerCase()) : null);

        // WO isn't in the work order panel, so check the schedule buckets
        if (!searchWorkOrder && this.dragAndDropService.dayBucketIndexRecords) {
          searchWorkOrder = this.dragAndDropService.dayBucketIndexRecords
            .map(sb => sb.scheduledBucketWellnessWorkOrders)
            .reduce((acc, curr) => acc.concat(curr), [])
            .map(sbwwo => sbwwo.wellnessWorkOrder)
            .find(x => x.quoteNumber === this.geoRegionWorkOrderNumber);
        }

        if (!searchWorkOrder && this.dragAndDropService.dayBucketIndexRecords) {
          searchWorkOrder = this.dragAndDropService.dayBucketIndexRecords
            .map(sb => sb.scheduledBucketWorkWorkOrders)
            .reduce((acc, curr) => acc.concat(curr), [])
            .map(sbwwo => sbwwo.workWorkOrder)
            .find(x => x.quoteNumber === this.geoRegionWorkOrderNumber);
        }

        if (searchWorkOrder && searchWorkOrder.address.latitude && searchWorkOrder.address.longitude) {
          this.geoRegionWorkOrderFindError = '';
          this.geoRegionWorkOrderAddressError = '';
          temp = this.workOrderFilter.filterByGeoRegion(temp, searchWorkOrder, this.geoRegionSearchRadius);
          temp.sort((a, b) => a.distance - b.distance);
        } else {
          if (!searchWorkOrder) {
            this.geoRegionWorkOrderFindError = 'Work Order not found.';
          } else if (!searchWorkOrder.address.latitude || !searchWorkOrder.address.longitude) {
            this.geoRegionWorkOrderFindError = 'Work Order does not have correct GPS coordinates.';
          }
        }
      }
      if (this.shouldFilterByPriority && this.selectedPriorities.length > 0) {
        temp = this.workOrderFilter.filterByPriority(temp, this.selectedPriorities.map(sp => sp.status));
      }
      
      if (this.shouldFilterByRM && this.selectedRMs.length > 0) {
        temp = this.workOrderFilter.filterByRM(temp, this.selectedRMs);
      }

      if (this.shouldFilterByEquipment && this.selectedEquipment) {
        temp = this.workOrderFilter.filterByEquipmentType(temp, this.selectedEquipment);
      }

      if (isWellnessOnly) {
        this.filteredWorkOrders = temp as FilteredWellnessWorkOrder[];
      } else if (isTreeWorkOnly) {
        this.filteredWorkOrders = temp as FilteredWorkWorkOrder[];
      } else {
        this.filteredWorkOrders = temp as FilteredQuoteWorkOrder[];
      }
    }

    if (forceRefresh) {
      this.forceRefresh.emit(true);
    } 
    else {
      this.OnWoFilteringFinished.emit(this.filteredWorkOrders);
    }
  }
}
